import { formatNumber } from "@/utils/number-utils";
import { ChevronDown } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  Button,
  DialogTrigger,
  ListBox,
  ListBoxItem,
  Selection,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import Badge from "../Badge";
import { Checkbox } from "./Checkbox";
import { Description, FieldError, Label } from "./Field";
import { SelectPopover } from "./Select";
import { Separator } from "./Separator";
import { composeTailwindRenderProps, focusRing } from "./utils";

export type MultiSelectItem<T> = {
  key: string;
  label?: string;
  value: T;
};

export interface MultiSelectProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string;
  items: MultiSelectItem<T>[];
  defaultSelectedKeys?: Selection;
  selectedKeys?: Selection;
  onSelectionChange?: (keys: Selection) => void;
  placeholder?: string;
  buttonClassName?: string;
  className?: string;
  "aria-label"?: string;
  allTitle?: string;
  allCount?: number;
  mode?: "default" | "compact";
  noCount?: boolean;
  isDisabled?: boolean;
}

export function MultiSelect<T>({
  label,
  description,
  errorMessage,
  items,
  defaultSelectedKeys,
  selectedKeys: controlledSelectedKeys,
  onSelectionChange,
  placeholder = "Vælg",
  buttonClassName,
  allTitle = "Vælg alle",
  allCount,
  noCount = false,
  mode = "default",
  isDisabled = false,
  className,
  ...props
}: MultiSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelectedKeys, setInternalSelectedKeys] = useState<Selection>(
    defaultSelectedKeys ?? new Set(),
  );
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedKeys = controlledSelectedKeys ?? internalSelectedKeys;
  const setSelectedKeys = onSelectionChange ?? setInternalSelectedKeys;

  const selectedItems = useMemo(() => {
    if (selectedKeys === "all") return items;
    return items.filter((item) => selectedKeys.has(item.key));
  }, [items, selectedKeys]);

  const totalCount = useMemo(() => {
    if (noCount) return undefined;
    if (allCount !== undefined) {
      return allCount;
    }

    return items.reduce((total, item) => {
      const labelParts = (item.label ?? item.key).split("|");

      const count = labelParts[1];

      return total + Number(count);
    }, 0);
  }, [items, allCount, noCount]);

  const removeSelectedKey = (key: string) => {
    const newSelectedKeys = new Set(selectedKeys);
    newSelectedKeys.delete(key);
    setSelectedKeys(newSelectedKeys);
  };

  const getDisplayLabel = (item: MultiSelectItem<T>) => {
    const label = item.label ?? item.key;
    return label.split("|")[0];
  };

  const selectionLabel = useMemo(() => {
    if (selectedItems.length === 0) return null;
    if (selectedItems.length === items.length) return "Alle";
    if (selectedItems.length === 1) {
      const label = selectedItems[0].label ?? selectedItems[0].key;
      return label.split("|")[0];
    }
    if (mode === "compact") {
      return selectedItems
        .map((item) => (item.label ?? item.key).split("|")[0])
        .join(", ");
    }
    return `${selectedItems.length} valgt`;
  }, [selectedItems, items.length, mode]);

  const isAllSelected =
    selectedItems.length === items.length && items.length > 0;

  const isSomeSelected =
    selectedItems.length > 0 && selectedItems.length < items.length;

  const handleSelectionChange = (keys: Selection) => {
    if (keys instanceof Set && keys.has("select-all")) {
      const newKeys = new Set(keys);
      newKeys.delete("select-all");

      if (!(selectedKeys instanceof Set && selectedKeys.has("select-all"))) {
        if (isAllSelected) {
          setSelectedKeys(new Set());
        } else {
          setSelectedKeys(new Set(items.map((item) => item.key)));
        }
        return;
      }
      setSelectedKeys(newKeys);
      return;
    }
    setSelectedKeys(keys);
  };

  return (
    <div className={twMerge("group flex flex-col", className)}>
      {label && <Label className="mb-2">{label}</Label>}

      <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        <Button
          ref={triggerRef}
          isDisabled={isDisabled}
          className={composeTailwindRenderProps(
            focusRing,
            twMerge(
              "group border-theme-outline-variant relative flex min-h-20 min-w-[200px] flex-wrap items-center gap-x-3 gap-y-3 rounded-sm border p-7 pr-10 font-bold transition-all [overflow-anchor:none] focus:scroll-m-0",
              buttonClassName,
              isDisabled && "cursor-not-allowed opacity-50",
            ),
          )}
          aria-label={props["aria-label"] ?? label}
          excludeFromTabOrder
        >
          {mode == "default" ? (
            <span
              className={twMerge(
                "line-clamp-1 text-start text-sm",
                selectionLabel
                  ? "text-theme-on-background"
                  : "text-theme-on-background-variant",
              )}
            >
              {selectionLabel || placeholder}
            </span>
          ) : (
            <>
              {selectedItems.length > 0 && !isAllSelected ? (
                selectedItems.map((item) => (
                  <span
                    key={item.key}
                    onPointerDownCapture={(e) => e.stopPropagation()}
                  >
                    <Badge
                      isDismissable={true}
                      remove={() => removeSelectedKey(item.key)}
                    >
                      {getDisplayLabel(item)}
                    </Badge>
                  </span>
                ))
              ) : (
                <span className="text-theme-on-glass text-sm">
                  {placeholder}
                </span>
              )}
            </>
          )}
          <ChevronDown
            aria-hidden
            className="text-theme-on-background absolute right-5 size-5 transition-transform duration-500 ease-in-out group-aria-expanded:[transform:scaleY(-1)]"
          />
        </Button>

        <SelectPopover
          triggerRef={triggerRef}
          className="min-w-(--trigger-width)"
          placement="bottom"
          offset={8}
        >
          <div>
            <ListBox
              aria-label={props["aria-label"] ?? label ?? "Select items"}
              selectionMode="multiple"
              selectedKeys={selectedKeys}
              onSelectionChange={handleSelectionChange}
              className="max-h-[inherit] outline-hidden"
              autoFocus="first"
              shouldFocusWrap
            >
              <ListBoxItem
                id="select-all"
                textValue={allTitle}
                className={(renderProps) =>
                  twMerge(
                    focusRing(renderProps),
                    "hover:bg-theme-on-background-hover focus:bg-theme-on-background-hover flex h-11 cursor-pointer items-center gap-x-3 rounded-md px-3 font-medium text-nowrap transition-[background-color] duration-200 focus:outline-none",
                    mode === "compact" ? "mb-1 pb-1" : "",
                  )
                }
              >
                {mode === "compact" ? (
                  <div className="flex h-9 w-full items-center justify-between gap-x-3">
                    <div className="relative flex flex-1 items-center gap-x-3">
                      <div className="pointer-events-none">
                        <Checkbox
                          size="sm"
                          className="-m-1 size-7 justify-center"
                          isSelected={isAllSelected}
                          isIndeterminate={isSomeSelected}
                        />
                      </div>
                      <div className="border-theme-outline-variant absolute -bottom-5.5 -left-1 w-full border-b" />

                      <span className="truncate text-[11px]">{allTitle}</span>
                    </div>
                    {totalCount !== undefined && totalCount !== 0 && (
                      <div className="relative">
                        <span className="text-[11px]">
                          {formatNumber(totalCount).replace(/,/g, ".")}
                        </span>
                        <div className="border-theme-outline-variant absolute -bottom-6 w-full border-b" />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="pointer-events-none">
                      <Checkbox
                        size="sm"
                        className="-m-1 size-7 justify-center"
                        isSelected={isAllSelected}
                        isIndeterminate={isSomeSelected}
                      />
                    </div>
                    <span className="flex-1 truncate text-[11px]">
                      {allTitle}
                    </span>
                  </>
                )}
              </ListBoxItem>

              {mode === "default" && <Separator className="my-3" />}

              {items.map((item, index) => {
                const labelParts = (item.label ?? item.key).split("|");
                const displayLabel = labelParts[0];
                const count = labelParts[1];

                return (
                  <ListBoxItem
                    key={item.key}
                    id={item.key}
                    textValue={displayLabel}
                    className={(renderProps) =>
                      twMerge(
                        focusRing(renderProps),
                        "hover:bg-theme-on-background-hover focus:bg-theme-on-background-hover flex h-11 w-full cursor-pointer items-center gap-x-3 rounded-md px-3 font-medium text-nowrap transition-[background-color] duration-200 focus:outline-none",
                        mode === "compact" && index === 0 ? "mt-4" : "",
                      )
                    }
                  >
                    {(renderProps) => (
                      <>
                        <div className="pointer-events-none">
                          <Checkbox
                            size="sm"
                            className="-m-1 size-7 justify-center"
                            isSelected={renderProps.isSelected}
                          />
                        </div>
                        <span className="flex-1 truncate text-[11px]">
                          {displayLabel}
                        </span>
                        {count && <span className="text-[11px]">{count}</span>}
                      </>
                    )}
                  </ListBoxItem>
                );
              })}
            </ListBox>
          </div>
        </SelectPopover>
      </DialogTrigger>

      {description && <Description>{description}</Description>}
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </div>
  );
}

export function useMultiSelect<T>(props: {
  items: MultiSelectItem<T>[];
  defaultSelectedKeys?: Selection;
}) {
  const [selectedKeys, setSelectedKeys] = useState<Selection>(
    props.defaultSelectedKeys ?? new Set(),
  );

  const selectedItems = useMemo(() => {
    if (selectedKeys === "all") return props.items;
    return props.items.filter((item) => selectedKeys.has(item.key));
  }, [props.items, selectedKeys]);

  const selectedValues = useMemo(() => {
    return selectedItems.map((item) => item.value);
  }, [selectedItems]);

  return {
    items: props.items,
    selectedKeys,
    setSelectedKeys,
    selectedItems,
    selectedValues,
  };
}
